import { IconButton, SearchField } from "@/components/ui";
import { Search } from "@/icons";
import { cn } from "@/lib/cn";
import { DURATION, TRANSITION } from "@/lib/motion";
import { motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useId, useRef } from "react";

const COLLAPSED_SIZE = "2.25rem";

export type ExpandingSearchProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name of the collapsed toggle. */
  openLabel?: string;
  /** Applied to the flex slot, not to the animated surface. */
  className?: string;
};

/**
 * Header search that morphs between an icon button and a full field.
 *
 * Controlled on purpose: the storefront drives it from atoms while `/menu` and
 * the category headers drive it from local state, and a single controlled
 * component serves all three instead of the three near-identical copies this
 * replaces.
 *
 * The surface animates its own width inside a `flex-1` slot, so the open field
 * spans exactly the space left by its siblings — alignment comes from the
 * layout rather than from the hand-tuned `ml-12` / `max-w-[90%]` offsets the
 * portaled version needed.
 */
export function ExpandingSearch({
  open,
  onOpenChange,
  value,
  onValueChange,
  placeholder = "Buscar produtos...",
  openLabel = "Buscar produtos",
  className,
}: ExpandingSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  const inputId = useId();

  const close = useCallback(() => {
    onValueChange("");
    onOpenChange(false);
  }, [onOpenChange, onValueChange]);

  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) inputRef.current?.focus();
    if (!open && wasOpen.current) toggleRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const transition = { ...TRANSITION.enter, duration: reduceMotion ? DURATION.none : TRANSITION.enter.duration };

  return (
    <div
      role="search"
      data-testid="header-search"
      data-open={open || undefined}
      className={cn("flex min-w-0 flex-1 items-center justify-end", className)}
    >
      <motion.div
        initial={false}
        animate={{ width: open ? "100%" : COLLAPSED_SIZE }}
        transition={transition}
        className="relative h-9 shrink-0"
      >
        <motion.div
          initial={false}
          animate={{ opacity: open ? 0 : 1 }}
          transition={{ duration: reduceMotion ? DURATION.none : DURATION.instant }}
          style={{ pointerEvents: open ? "none" : "auto" }}
          className="absolute inset-0 z-raised"
        >
          <IconButton
            ref={toggleRef}
            icon={Search}
            variant="ghost"
            tone="primary"
            size="icon-bar"
            label={openLabel}
            aria-expanded={open}
            aria-controls={inputId}
            onClick={() => onOpenChange(true)}
          />
        </motion.div>
        <div
          className={cn("absolute inset-0 flex items-center", !open && "invisible overflow-hidden")}
          style={{ pointerEvents: open ? "auto" : "none" }}
        >
          <SearchField
            id={inputId}
            ref={inputRef}
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            clearable="always"
            onClear={close}
            clearLabel="Fechar busca"
            glyph="md"
            placeholder={placeholder}
            aria-label={placeholder}
            tabIndex={open ? 0 : -1}
            className="min-w-0 flex-1"
          />
        </div>
      </motion.div>
    </div>
  );
}
